import React, { useState, useEffect } from "react";

import Select from "react-select";
import { isEmpty } from "lodash";
import { useTranslation, Trans } from "react-i18next";

import BaseModal from "./BaseModal";
import SimpleNotification from "../Notifications/SimpleNotification";
import ModelVersionsAPI from "../../API/modelVersions.api";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import InformationTooltip from "../Tooltips/InformationTooltip";
import { compressFile } from "../../utils/ZipHelpers";
import ExternalRequest from "../../API/ExternalRequest";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { getReactSelectStyling } from "../../utils/ReactSelectStyles";

const ModelBulkUploadModal = ({
    isVisible,
    setIsVisible,
    isLoading,
    setIsLoading,
    deployedMaterialParameters,
}) => {
    //Data states
    const [parameters, setParameters] = useState([]);
    const [selectedParameters, setSelectedParameters] = useState([]);

    const [materials, setMaterials] = useState([]);
    const [selectedMaterials, setSelectedMaterials] = useState([]);

    const [materialsDict, setMaterialsDict] = useState({});
    const [disabledMaterialsDict, setDisabledMaterialsDict] = useState({});

    const [willExceed, setWillExceed] = useState(false);

    const [importedData, setImportedData] = useState("");

    const { t } = useTranslation();

    const PARAMETERS_MAX = 15;

    const fields = [
        {
            label: t("material"),
            element: () => (
                <Select
                    isMulti
                    isClearable={false}
                    options={materials}
                    value={selectedMaterials}
                    isOptionDisabled={(option) =>
                        disabledMaterialsDict[option.value] ||
                        isParametersFull()
                    }
                    onChange={handleMaterialOnChange}
                    placeholder={t("selectPlaceholder", {
                        entity: t("material"),
                    })}
                    styles={getReactSelectStyling({
                        isMultiSelect: true,
                    })}
                />
            ),
        },
        {
            infoTooltip: t("eigenvectorNotSupportedMessage"),
            label: t("parameter"),
            element: () => (
                <Select
                    isMulti
                    isClearable={false}
                    options={parameters}
                    value={selectedParameters}
                    isOptionDisabled={(option) =>
                        option.isEigenvector || isParametersFull()
                    }
                    onChange={handleParameterOnChange}
                    placeholder={t("selectPlaceholder", {
                        entity: t("parameter"),
                    })}
                    styles={getReactSelectStyling({
                        isMultiSelect: true,
                    })}
                />
            ),
        },
    ];

    useEffect(() => {
        if (!isEmpty(deployedMaterialParameters)) {
            formatMaterialParameters(deployedMaterialParameters);
        }
    }, [deployedMaterialParameters]);

    useEffect(() => {
        setWillExceed(false);
    }, [selectedParameters, selectedMaterials]);

    const getImportDetails = async () => {
        const result = await ModelVersionsAPI.importVersionDetails();
        return { url: result?.url, path: result?.path };
    };

    const handleSubmit = () => {
        getImportDetails()
            .then((details) => {
                importToS3(importedData, details.url, details.path);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const importToS3 = async (zipFile, importingUrl, importingPath) => {
        try {
            setIsLoading(true);
            const result = await ExternalRequest.put(importingUrl, zipFile);
            if (result) submitFilePath(importingPath);
            else {
                setIsLoading(false);
                ErrorAlert(t("requestError"));
            }
        } catch (error) {
            ErrorAlert(t("unhandledException"));
            console.log(error);
        }
    };

    const submitFilePath = async (importingPath) => {
        const result = await ModelVersionsAPI.uploadVersions({
            path: importingPath,
        });

        setIsLoading(false);
        if (result.inserted) {
            setIsVisible(false);
            resetForm();
            SuccessAlert(t("uploadVersionSuccessMessage"));
        } else if (result.expired) {
            ErrorAlert(t("expiredFile"));
            getImportDetails();
        }
    };

    const getExportCsv = async () => {
        setIsLoading(true);
        const result = await ModelVersionsAPI.exportVersionDetails(
            selectedParameters.map((para) => para.value)
        );
        if (result.url) handleExternalLink(result.url);
        setIsLoading(false);
    };

    const resetForm = () => {
        setSelectedParameters([]);
        setSelectedMaterials([]);
        setWillExceed(false);
        setImportedData("");
    };

    const formatMaterialParameters = (materialParameters) => {
        const dict = {};
        const disabledDict = {};
        const parameters = [];
        const materials = [];

        materialParameters.forEach((materialParameter) => {
            let isMaterialEigenvector = true;

            dict[materialParameter.value] = materialParameter.parameters
                .filter((param) => {
                    isMaterialEigenvector =
                        isMaterialEigenvector && param.isEigenvector;
                    if (!param.isEigenvector) return param;
                })
                .map((param) => ({
                    ...param,
                    label: `${materialParameter.label} - ${param.label}`,
                    materialId: materialParameter.value,
                    materialName: materialParameter.label,
                }));

            disabledDict[materialParameter.value] = isMaterialEigenvector;

            parameters.push(
                ...materialParameter.parameters.map((param) => ({
                    ...param,
                    label: `${materialParameter.label} - ${param.label}`,
                    materialId: materialParameter.value,
                    materialName: materialParameter.label,
                }))
            );
            materials.push({
                label: materialParameter.label,
                value: materialParameter.value,
            });
        });

        setParameters(parameters);
        setMaterials(materials);
        setMaterialsDict(dict);
        setDisabledMaterialsDict(disabledDict);
    };

    const handleFileClick = (id) => {
        const element = document.getElementById(id);
        element.click();
        element.value = "";
    };

    const handleParameterOnChange = (_, action) => {
        if (action.action === "select-option") {
            handleAddParameter(action.option);
        } else if (action.action === "remove-value") {
            handleRemoveParameter(action.removedValue);
        }
    };

    const handleAddParameter = (parameter) => {
        //Add parameter to selected parameters
        setSelectedParameters((prevParams) => [...prevParams, parameter]);

        //Remove parameter from parameter options
        setParameters((prevParams) =>
            prevParams.filter((param) => param.value !== parameter.value)
        );

        addMaterialByParameter(parameter);
    };

    const handleRemoveParameter = (parameter) => {
        //Remove parameter from selected parameters
        setSelectedParameters((prevParams) =>
            prevParams.filter((param) => param.value !== parameter.value)
        );

        //Re-add parameter to parameter options
        setParameters((prevParams) => [parameter, ...prevParams]);

        removeMaterialByParameter(parameter);
    };

    //add material by adding parameters
    const addMaterialByParameter = (parameter) => {
        let materialExists = false;
        let materialParamsCount = 0;

        //check wether the material is already selected or not and the number of added parameters
        //that belongs to the same material
        selectedMaterials.forEach((mat) => {
            if (mat.value === parameter.materialId) {
                materialExists = true;
            }
        });

        materialExists &&
            selectedParameters.forEach((param) => {
                if (param.materialId === parameter.materialId)
                    materialParamsCount++;
            });

        //check if material does not exist then add it to the selected materials
        if (!materialExists) {
            setMaterials((prevMaterials) =>
                prevMaterials.filter(
                    (mat) => mat.value !== parameter.materialId
                )
            );

            setSelectedMaterials((prevMaterials) => [
                ...prevMaterials,
                {
                    value: parameter.materialId,
                    label:
                        materialParamsCount + 1 ===
                        materialsDict[parameter.materialId].length
                            ? getFullMaterialLabel(parameter.materialName)
                            : parameter.materialName,
                },
            ]);

            //otherwise compare the number of selected params if matches the number of all parameters
            //of the material then check it as full
        } else if (
            materialParamsCount + 1 ===
            materialsDict[parameter.materialId].length
        ) {
            setSelectedMaterials((prevMaterials) => {
                return prevMaterials.map((mat) => {
                    return mat.value === parameter.materialId
                        ? {
                              value: parameter.materialId,
                              label: getFullMaterialLabel(
                                  parameter.materialName
                              ),
                          }
                        : mat;
                });
            });
        }
    };

    const removeMaterialByParameter = (parameter) => {
        let sameMaterialCounter = 0;

        for (let param of selectedParameters) {
            if (param.materialId === parameter.materialId)
                sameMaterialCounter++;
        }

        if (sameMaterialCounter < 2) {
            setSelectedMaterials((prevMaterials) => {
                return prevMaterials.filter(
                    (mat) => mat.value !== parameter.materialId
                );
            });

            setMaterials((prevMaterials) => [
                { label: parameter.materialName, value: parameter.materialId },
                ...prevMaterials,
            ]);
        } else {
            setSelectedMaterials((prevMaterials) => {
                return prevMaterials.map((mat) => {
                    return mat.value === parameter.materialId
                        ? {
                              value: parameter.materialId,
                              label: parameter.materialName,
                          }
                        : mat;
                });
            });
        }
    };

    const handleMaterialOnChange = (_, action) => {
        if (action.action === "select-option") {
            handleAddMaterial(action.option);
        } else if (action.action === "remove-value") {
            handleRemoveMaterial(action.removedValue);
        }
    };

    const handleAddMaterial = (material) => {
        if (
            materialsDict[material.value].length + selectedParameters.length >
            PARAMETERS_MAX
        ) {
            setWillExceed(true);
        } else {
            //Add material to selected materials
            setSelectedMaterials((prevMaterials) => [
                ...prevMaterials,
                { ...material, label: getFullMaterialLabel(material.label) },
            ]);

            //Add material's parameters to selected parameters
            setSelectedParameters((prevParams) => [
                ...prevParams,
                ...materialsDict[material.value],
            ]);

            //Remove material from material options
            setMaterials((prevMaterials) =>
                prevMaterials.filter((mat) => mat.value !== material.value)
            );

            //Remove parameters from parameter options
            setParameters((prevParams) =>
                prevParams.filter(
                    (param) =>
                        param.materialId !== material.value ||
                        param.isEigenvector
                )
            );
        }
    };

    const handleRemoveMaterial = (material) => {
        const filteredParameters = [];
        const newSelectedParameters = [];

        //Remove material from selected materials
        setSelectedMaterials((prevMaterials) =>
            prevMaterials.filter((mat) => mat.value !== material.value)
        );

        //Remove material's parameters from selected parameters
        selectedParameters.forEach((param) => {
            if (param.materialId !== material.value)
                newSelectedParameters.push(param);
            else filteredParameters.push(param);
        });
        setSelectedParameters(newSelectedParameters);

        //Re-add removed material to the material options
        setMaterials((prevMaterials) => [
            {
                ...material,
                label: materialsDict[material.value][0].materialName,
            },
            ...prevMaterials,
        ]);

        //Re-add removed material's parameters to the parameters options
        setParameters((prevParams) => [...filteredParameters, ...prevParams]);
    };

    const getFullMaterialLabel = (materialName) => {
        return (
            <>
                {materialName}
                <i className="fas fa-check-circle text-green-700 ml-1"></i>
            </>
        );
    };

    const isParametersFull = () => PARAMETERS_MAX === selectedParameters.length;

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    return (
                        <div className="tags input-group" key={index}>
                            <div className="flex">
                                <h4 className="uppercase text-xs required">
                                    {field.label}
                                </h4>
                                {field.infoTooltip && (
                                    <div className="text-xs ml-1">
                                        <InformationTooltip
                                            message={field.infoTooltip}
                                        />
                                    </div>
                                )}
                            </div>

                            {field.element()}
                        </div>
                    );
                }
            })
        );
    };

    const handleFileReader = (event) => {
        let reader = new FileReader();
        reader.readAsBinaryString(event.target.files[0]);
        reader.onload = () => {
            const buffer = Buffer.from(reader.result, "binary");
            const zipFile = compressFile(buffer, event.target.files[0].name);
            setImportedData(zipFile.toBuffer());
        };
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isLoading}
                action={{
                    name: t("upload"),
                    submitAction: handleSubmit,
                    disabled: !importedData,
                }}
                isLoading={isLoading}
                footerHorizontalLine={true}
                title={<Trans i18nKey="uploadModels">Upload Models</Trans>}
                titleIcon="fas fa-upload"
            >
                <div className="main-content-header flex items-start justify-between">
                    <p className="mr-4">
                        <Trans i18nKey="bulkUploadModelsDescription">
                            Select parameters in order to export a csv file that
                            contains version details, modify details and import
                            it.
                        </Trans>
                    </p>

                    <div className="flex items-center">
                        <button
                            className="default-orange-outline-btn flex items-center"
                            onClick={() => handleFileClick("importFile")}
                            id="bulk-upload-modal-import-btn"
                        >
                            <i className="fas fa-file-upload" />
                            <span className="ml-2">
                                <Trans i18nKey="import">Import</Trans>
                            </span>
                            <input
                                id="importFile"
                                type="file"
                                name="data"
                                accept=".csv"
                                className="hidden"
                                onChange={handleFileReader}
                            />
                        </button>
                        <button
                            onClick={getExportCsv}
                            className={`default-orange-outline-btn flex items-center ml-2 ${
                                isEmpty(selectedParameters) || isLoading
                                    ? "disabled"
                                    : ""
                            } `}
                            id="bulk-upload-modal-export-btn"
                        >
                            <div className="flex items-center">
                                <i className="fas fa-file-download" />
                                <span className="ml-2">
                                    <Trans i18nKey="export">Export</Trans>
                                </span>
                            </div>
                        </button>
                    </div>
                </div>
                <div className="mt-6 px-2">
                    {renderFields()}
                    {isParametersFull() && (
                        <SimpleNotification message={t("parametersLimit")} />
                    )}
                    {willExceed && (
                        <SimpleNotification message={t("willExceedMessage")} />
                    )}
                </div>
            </BaseModal>
        </>
    );
};

export default ModelBulkUploadModal;
